/**
 * PhaseDetailsRenderer — Renders phaseDetails JSON dynamically.
 *
 * phaseDetails is a JSON string whose shape varies per status/client.
 * This component parses it and renders based on value types:
 * - string/number/boolean → key-value pair
 * - array of primitives → comma-separated list
 * - array of objects → inline table
 * - nested object → recursive key-value rendering
 * - null/undefined → skipped
 *
 * Falls back to raw JSON display if parsing fails.
 */
import Box from '@cloudscape-design/components/box';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';

interface PhaseDetailsRendererProps {
  rawJson: string;
}

/** Renders an array of objects as a Cloudscape Table. */
const ArrayTable = ({ items }: { items: Record<string, any>[] }) => {
  if (items.length === 0) return <Box color="text-body-secondary">Empty list</Box>;

  // Derive columns from all keys across all objects
  const keySet = new Set<string>();
  items.forEach(obj => Object.keys(obj).forEach(k => keySet.add(k)));
  const allKeys = Array.from(keySet);

  const columnDefinitions = allKeys.map(key => ({
    id: key,
    header: key.replace(/_/g, ' '),
    cell: (item: Record<string, any>) => String(item[key] ?? '—'),
  }));

  return (
    <Table
      variant="embedded"
      sortingDisabled
      resizableColumns
      columnDefinitions={columnDefinitions}
      items={items}
      empty={<Box color="text-body-secondary">No data</Box>}
    />
  );
};

/** Renders a single parsed value based on its type. */
const renderValue = (value: any): React.ReactNode => {
  if (value == null) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    // Array of objects → table
    if (typeof value[0] === 'object' && value[0] !== null) {
      return <ArrayTable items={value} />;
    }
    // Array of primitives → comma list
    return value.join(', ');
  }
  if (typeof value === 'object') {
    // Nested object → recursive key-value pairs
    return <ObjectRenderer data={value} />;
  }
  return String(value);
};

/** Renders an object as Cloudscape KeyValuePairs. */
const ObjectRenderer = ({ data }: { data: Record<string, any> }) => {
  const entries = Object.entries(data).filter(([, v]) => v != null);
  if (entries.length === 0) return <Box color="text-body-secondary">No details</Box>;

  const items = entries.map(([key, value]) => ({
    label: key.replace(/_/g, ' '),
    value: renderValue(value),
  }));

  return <KeyValuePairs columns={2} items={items} />;
};

export const PhaseDetailsRenderer = ({ rawJson }: PhaseDetailsRendererProps) => {
  let parsed: any;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    // If it's not valid JSON, display as raw string
    return (
      <SpaceBetween size="xs">
        <Box fontSize="body-s" color="text-body-secondary">{rawJson}</Box>
      </SpaceBetween>
    );
  }

  // If parsed is a primitive, just show it
  if (parsed == null || typeof parsed !== 'object') {
    return <Box>{String(parsed)}</Box>;
  }

  // If parsed is an array
  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return <Box color="text-body-secondary">Empty list</Box>;
    if (typeof parsed[0] === 'object') return <ArrayTable items={parsed} />;
    return <Box>{parsed.join(', ')}</Box>;
  }

  // Object → render as key-value pairs
  return <ObjectRenderer data={parsed} />;
};
